import { useCallback, useEffect, useState, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Highlight from "@tiptap/extension-highlight";
import Typography from "@tiptap/extension-typography";
import Image from "@tiptap/extension-image";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { common, createLowlight } from "lowlight";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Highlighter,
  Save,
  Loader2,
  Users,
  Server,
  ImageIcon,
  Check,
} from "lucide-react";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const lowlight = createLowlight(common);

const LANGUAGES = [
  { value: "plaintext", label: "Plain Text" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "python", label: "Python" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "json", label: "JSON" },
  { value: "markdown", label: "Markdown" },
  { value: "bash", label: "Bash" },
  { value: "sql", label: "SQL" },
  { value: "php", label: "PHP" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "go", label: "Go" },
  { value: "rust", label: "Rust" },
  { value: "ruby", label: "Ruby" },
  { value: "xml", label: "XML" },
  { value: "yaml", label: "YAML" },
];

const MenuButton = ({ active, onClick, children, title }) => (
  <button
    onClick={onClick}
    className={cn(
      "p-2 rounded-md transition-colors",
      active
        ? "bg-primary text-primary-foreground"
        : "hover:bg-muted text-foreground"
    )}
    title={title}
  >
    {children}
  </button>
);

export const Editor = ({ content, fileName, onSave, isSaving, fileContext = "personal", onContentChange, searchRequest }) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("plaintext");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  
  const pendingContentRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const editorContainerRef = useRef(null);
  const isSavingRef = useRef(false);
  const lastLoadedContentRef = useRef(null);
  const editorInitializedRef = useRef(false);
  const lastLoadedFileNameRef = useRef(fileName);
  
  // Ref folosit pentru evaluare sincronă (înainte de mount/unmount)
  const hasChangesRef = useRef(false);

  // --- 1. Avertizare la refresh sau închiderea completă a paginii ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChangesRef.current) {
        e.preventDefault();
        e.returnValue = "Ai modificări nesalvate. Ești sigur că vrei să ieși?"; 
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // --- 2. HOOK DE SIGURANȚĂ (FAIL-SAFE) PENTRU SCHIMBAREA FIȘIERULUI ---
  useEffect(() => {
    const fallbackSave = onSave; // Capturăm funcția onSave corespunzătoare fișierului curent
    
    return () => {
      // Această zonă (cleanup) rulează EXACT atunci când componenta se pregătește să schimbe 'fileName' sau să dispară.
      if (hasChangesRef.current && pendingContentRef.current) {
        console.log("Salvare de urgență înainte de schimbarea fișierului:", fileName);
        fallbackSave(pendingContentRef.current).catch(err => console.error(err));
        hasChangesRef.current = false;
      }
    };
  }, [fileName, onSave]);

  const uploadImage = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    
    try {
      setIsUploadingImage(true);
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data.url;
    } catch (error) {
      console.error("Image upload failed:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setIsUploadingImage(false);
    }
  }, []);

  const handlePaste = useCallback((view, event) => {
    const items = Array.from(event.clipboardData?.items || []);
    
    const hasImage = items.some(item => item.type.startsWith("image/"));
    const hasText = items.some(item => item.type === "text/plain" || item.type === "text/html");

    if (hasImage && !hasText) {
      event.preventDefault();
      const imageItem = items.find(item => item.type.startsWith("image/"));
      const file = imageItem?.getAsFile();
      
      if (file) {
        uploadImage(file).then((url) => {
          if (url) {
            const { state, dispatch } = view;
            const imageNode = state.schema.nodes.image.create({ src: url });
            dispatch(state.tr.replaceSelectionWith(imageNode));
          }
        });
      }
      return true;
    }

    return false;
  }, [uploadImage]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Placeholder.configure({
        placeholder: "Start writing...",
      }),
      Highlight.configure({
        multicolror: true,
      }),
      Typography,
      Image.configure({
        HTMLAttributes: {
          class: "editor-image",
        },
        allowBase64: true,
      }),
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: "plaintext",
        exitOnTripleEnter: false,
        exitOnArrowDown: false,
      }),
    ],
    content: content || "",
    editorProps: {
      attributes: {
        class: "tiptap-editor outline-none min-h-full w-full",
      },
      clipboardTextSerializer: (slice) => {
        // Extrage textul pur și înlocuiește break-urile normale cu formatul \r\n necesar terminalelor
        const text = slice.content.textBetween(0, slice.content.size, '\n');
        return text.split(/\r?\n/).join('\r\n');
      },
      handlePaste: handlePaste,
      handleKeyDown: (view, event) => {
        const { state } = view;
        const { selection } = state;
        const { $from, $to } = selection;
        
        const codeBlock = $from.node($from.depth);
        const isInCodeBlock = codeBlock && codeBlock.type.name === 'codeBlock';

        // --- NOU: SCURTĂTURA CTRL + D PENTRU DUPLICAREA RÂNDULUI ---
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd') {
          event.preventDefault(); // Prevenim comportamentul browserului (Bookmarking)

          if (isInCodeBlock) {
            const text = codeBlock.textContent;
            const posInsideBlock = $from.pos - $from.start();
            const toInsideBlock = $to.pos - $from.start();

            // Găsim începutul și sfârșitul rândului/rândurilor selectate
            let lineStart = text.lastIndexOf('\n', posInsideBlock - 1) + 1;
            let lineEnd = text.indexOf('\n', toInsideBlock);
            if (lineEnd === -1) lineEnd = text.length;

            const lineText = text.substring(lineStart, lineEnd);
            
            // Inserăm textul duplicat exact sub el
            const tr = state.tr.insertText('\n' + lineText, $from.start() + lineEnd);
            view.dispatch(tr);
            return true;
          } else {
            // Dacă nu suntem în code block, duplicăm nodurile bloc selectate (paragrafe, etc)
            if ($from.depth > 0 && $to.depth > 0) {
              const startPos = $from.before($from.depth);
              const endPos = $to.after($to.depth);

              const slice = state.doc.slice(startPos, endPos);
              const tr = state.tr.insert(endPos, slice.content);
              view.dispatch(tr);
              return true;
            }
          }
          return false;
        }

        // --- SCURTĂTURA CTRL + Q PENTRU COMENTARII (TOGGLE INDIVIDUAL PE FIECARE RÂND) ---
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'q') {
          event.preventDefault();
          
          const { state, dispatch } = view;
          const { $from, $to } = state.selection;
          const tr = state.tr;
          let modified = false;

          const blocks = [];
          state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            if (node.isBlock && node.isTextblock) {
              blocks.push({ node, pos });
            }
          });

          // Parcurgem invers blocurile pentru a păstra coordonatele (indexii) intacte
          for (let i = blocks.length - 1; i >= 0; i--) {
            const { node, pos } = blocks[i];
            
            let prefix = "# ";
            let suffix = "";
            
            if (node.type.name === "codeBlock") {
              const lang = node.attrs.language || "plaintext";
              if (["javascript", "typescript", "c", "cpp", "csharp", "java", "go", "rust", "php", "json"].includes(lang)) {
                prefix = "// ";
              } else if (["html", "xml"].includes(lang)) {
                prefix = ""; 
              } else if (["css"].includes(lang)) {
                prefix = "/* "; 
                suffix = " */";
              } else if (["sql"].includes(lang)) {
                prefix = "-- ";
              }
            }

            const pTrim = prefix.trim();
            const sTrim = suffix.trim();
            
            if (!pTrim) continue; // Dacă limbajul nu suportă, sărim

            if (node.type.name === "codeBlock") {
              const text = node.textContent;
              const lines = text.split('\n');
              const blockStart = pos + 1;
              
              // Cât din block e efectiv selectat
              const selStart = Math.max(0, $from.pos - blockStart);
              const selEnd = Math.min(text.length, $to.pos - blockStart);

              let newText = [];
              let currentOffset = 0;
              let blockModified = false;

              // Generăm noul text linie cu linie în memorie
              for (let j = 0; j < lines.length; j++) {
                const line = lines[j];
                const lineStart = currentOffset;
                const lineEnd = currentOffset + line.length;
                const isSelected = (selStart <= lineEnd && selEnd >= lineStart);

                if (isSelected && line.trim() !== "") {
                  const trimmedLine = line.trimStart();
                  const leadingSpaces = line.substring(0, line.length - trimmedLine.length);
                  
                  const isCommented = suffix 
                    ? (trimmedLine.startsWith(pTrim) && trimmedLine.endsWith(sTrim))
                    : trimmedLine.startsWith(pTrim);

                  if (isCommented) {
                    // Ștergem comentariul
                    let uncommented = trimmedLine;
                    if (suffix) {
                      const actualSuffix = uncommented.endsWith(suffix) ? suffix : sTrim;
                      uncommented = uncommented.slice(0, -actualSuffix.length);
                    }
                    const actualPrefix = uncommented.startsWith(prefix) ? prefix : pTrim;
                    uncommented = uncommented.slice(actualPrefix.length);
                    
                    newText.push(leadingSpaces + uncommented);
                  } else {
                    // Adăugăm comentariul
                    newText.push(leadingSpaces + prefix + trimmedLine + suffix);
                  }
                  blockModified = true;
                  modified = true;
                } else {
                  // Linia nu este selectată sau e goală, o păstrăm intactă
                  newText.push(line);
                }
                currentOffset += line.length + 1;
              }

              if (blockModified) {
                // Înlocuim tot textul din block simultan, evitând stricarea pozițiilor
                tr.insertText(newText.join('\n'), blockStart, blockStart + text.length);
              }
            } else {
              // Pentru Paragrafe normale (nu de cod)
              const text = node.textContent;
              if (text.trim() === "") continue;

              const leadingSpaces = text.length - text.trimStart().length;
              const absTextStart = pos + 1 + leadingSpaces;
              const trimmedLine = text.trimStart();

              if (trimmedLine.startsWith(pTrim)) {
                const actualPrefix = trimmedLine.startsWith(prefix) ? prefix : pTrim;
                tr.delete(absTextStart, absTextStart + actualPrefix.length);
                modified = true;
              } else {
                tr.insertText(prefix, absTextStart);
                modified = true;
              }
            }
          }

          if (modified) {
            tr.setSelection(state.selection.map(tr.doc, tr.mapping));
            view.dispatch(tr);
            return true;
          }
          return false;
        }
        
        if ((event.ctrlKey || event.metaKey) && event.key === 'a' && isInCodeBlock) {
          event.preventDefault();
          const start = $from.start($from.depth);
          const end = $from.end($from.depth);
          const tr = state.tr.setSelection(
            state.selection.constructor.create(state.doc, start, end)
          );
          view.dispatch(tr);
          return true;
        }
        
        if (event.key === 'Enter' && isInCodeBlock && !event.shiftKey) {
          return false;
        }
        
        if (event.key === 'Enter' && event.shiftKey && isInCodeBlock) {
          event.preventDefault();
          const endOfBlock = $from.end($from.depth);
          const tr = state.tr.insert(endOfBlock + 1, state.schema.nodes.paragraph.create());
          tr.setSelection(state.selection.constructor.near(tr.doc.resolve(endOfBlock + 2)));
          view.dispatch(tr);
          return true;
        }
        
        return false;
      },
      handleDrop: (view, event, slice, moved) => {
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          for (const file of files) {
            if (file.type.startsWith("image/")) {
              event.preventDefault();
              uploadImage(file).then((url) => {
                if (url && editor) {
                  editor.chain().focus().setImage({ src: url }).run();
                }
              });
              return true;
            }
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      setHasChanges(true);
      hasChangesRef.current = true; // Sincronizare la literă
      
      const { state, view } = editor;
      let tr = state.tr;
      let docModified = false;

      // Logica de separare automată a blocurilor de cod lipite
      state.doc.descendants((node, pos) => {
        if (docModified) return false; // Oprim parcurgerea după prima modificare
        
        if (node.type.name === "codeBlock") {
          const nextNodePos = pos + node.nodeSize;
          const nextNode = state.doc.nodeAt(nextNodePos);
          
          if (nextNode && nextNode.type.name === "codeBlock") {
            tr.insert(nextNodePos, state.schema.nodes.paragraph.create());
            docModified = true;
          }
        }
      });

      // Verificăm dacă documentul se termină cu un codeBlock. Adăugăm un rând gol la final ca să se poată scrie
      if (!docModified) {
        const lastChild = state.doc.lastChild;
        if (lastChild && lastChild.type.name === "codeBlock") {
          tr.insert(state.doc.content.size, state.schema.nodes.paragraph.create());
          docModified = true;
        }
      }

      if (docModified) {
        view.dispatch(tr);
        return; // Oprim onUpdate-ul curent, va fi reluat automat după dispatch
      }

      const currentContent = editor.getHTML();
      pendingContentRef.current = currentContent;
      
      if (onContentChange) {
        onContentChange(currentContent);
      }
    },
  });

  useEffect(() => {
    if (editor && content !== undefined) {
      // Forțăm editorul să se re-inițializeze dacă s-a schimbat conținutul SAU dacă i-ai schimbat numele din 'untitled'
      const isNewFile = content !== lastLoadedContentRef.current || fileName !== lastLoadedFileNameRef.current;
      
      if (!editorInitializedRef.current || isNewFile) {
        editor.commands.setContent(content || "");
        lastLoadedContentRef.current = content;
        lastLoadedFileNameRef.current = fileName;
        editorInitializedRef.current = true;
        
        setHasChanges(false);
        hasChangesRef.current = false;
        pendingContentRef.current = null;
      }
    }
  }, [content, editor, fileName]);

  useEffect(() => {
    if (!editor || !searchRequest) return;

    let target = "";
    if (searchRequest.query) {
       target = searchRequest.query.trim().toLowerCase();
    } else if (searchRequest.snippet) {
       const words = searchRequest.snippet.replace(/[^a-zA-Z0-9]/g, ' ').split(/\s+/);
       target = words.reduce((a, b) => a.length > b.length ? a : b, "").toLowerCase();
    }

    if (!target || target.length < 2) return;

    const timeout = setTimeout(() => {
      const matches = [];

      editor.state.doc.descendants((node, pos) => {
        if (node.isBlock && node.textContent) {
          const textLower = node.textContent.toLowerCase();
          
          if (node.type.name === 'codeBlock') {
            const lines = textLower.split('\n');
            let currentOffset = 0;
            
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i];
              if (line.includes(target)) {
                const startPos = pos + 1 + currentOffset;
                const endPos = startPos + line.length;
                matches.push({ from: startPos, to: endPos });
              }
              currentOffset += line.length + 1; 
            }
          } else {
            let searchStart = 0;
            let matchIdx = textLower.indexOf(target, searchStart);

            while (matchIdx !== -1) {
              const startSel = pos + 1;
              const endSel = pos + node.nodeSize - 1;
              
              if (!matches.some(m => m.from === startSel && m.to === endSel)) {
                  matches.push({ from: startSel, to: endSel });
              }
              
              searchStart = matchIdx + target.length;
              matchIdx = textLower.indexOf(target, searchStart);
            }
          }
        }
      });

      if (matches.length > 0) {
        let targetIndex = searchRequest.matchIndex !== undefined ? searchRequest.matchIndex : 0;

        if (targetIndex >= matches.length) {
          targetIndex = Math.max(0, matches.length - 1);
        }

        const selectedMatch = matches[targetIndex];

        editor.chain()
          .focus()
          .setTextSelection({ from: selectedMatch.from, to: selectedMatch.to })
          .run();

        setTimeout(() => {
          try {
            const dom = editor.view.domAtPos(selectedMatch.from);
            const element = dom.node.nodeType === Node.TEXT_NODE ? dom.node.parentElement : dom.node;
            
            if (element && element.scrollIntoView) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          } catch (e) {
            console.log("Eroare la centrarea scroll-ului:", e);
          }
        }, 50); 
      }
    }, 400); 

    return () => clearTimeout(timeout);
  }, [editor, searchRequest, content]);
  
  useEffect(() => {
    if (!editor || !fileName || !editorContainerRef.current) return;

    const scrollContainer = editorContainerRef.current;
    const storageKey = `scroll_pos_${fileName}`;
    const isSearchingNow = searchRequest && searchRequest.query;
    
    let isRestoring = false;

    const applySavedScroll = () => {
      const savedScroll = sessionStorage.getItem(storageKey);
      if (savedScroll !== null && !isSearchingNow) {
        const targetPos = parseInt(savedScroll, 10);
        if (scrollContainer.scrollHeight >= targetPos) {
           scrollContainer.scrollTop = targetPos;
           return true; 
        }
      }
      return false;
    };

    const resizeObserver = new ResizeObserver(() => {
      if (!isSearchingNow) {
        isRestoring = true;
        applySavedScroll();
        setTimeout(() => { isRestoring = false; }, 500);
      }
    });

    const proseMirror = scrollContainer.querySelector('.ProseMirror');
    if (proseMirror) resizeObserver.observe(proseMirror);

    let scrollTimer = null;
    const handleScroll = () => {
      if (isRestoring || isSearchingNow) return;

      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const currentPos = Math.max(0, Math.min(scrollContainer.scrollTop, maxScroll));
        
        sessionStorage.setItem(storageKey, currentPos.toString());
      }, 200);
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [editor, fileName, searchRequest]);

  const performSave = useCallback(async () => {
    if (!editor || isSavingRef.current) return;
    
    const contentToSave = editor.getHTML();
    
    if (!contentToSave) return;
    
    isSavingRef.current = true;
    
    try {
      await onSave(contentToSave);
      setLastSavedAt(new Date());
      
      const currentEditorContent = editor.getHTML();
      if (currentEditorContent === contentToSave) {
        setHasChanges(false);
        hasChangesRef.current = false; // Sincronizare la finalul salvării
      }
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      isSavingRef.current = false;
    }
  }, [editor, onSave]);

  useEffect(() => {
    if (!hasChanges || !editor) return;
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      performSave();
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [hasChanges, editor, performSave]);

  const handleManualSave = useCallback(() => {
    if (editor) {
      pendingContentRef.current = editor.getHTML();
      performSave();
    }
  }, [editor, performSave]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleManualSave();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleManualSave]);

  // LOGICA NOUĂ PENTRU CLICK EFICIENT:
  const handleEditorAreaClick = useCallback((e) => {
    if (!editor || !editor.view) return;

    // 1. Ignorăm click-ul dacă utilizatorul selectează manual text cu drag and drop
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const pmNode = editor.view.dom;
    const pmRect = pmNode.getBoundingClientRect();
    const lastChild = pmNode.lastElementChild;

    // 2. CLICK SUB TEXT -> Focus la final / Creare rând automat
    if (lastChild) {
      const lastChildRect = lastChild.getBoundingClientRect();
      // Verificăm dacă click-ul a fost dat mai jos de ultimul rând efectiv
      if (e.clientY > lastChildRect.bottom + 10) { 
        const lastNode = editor.state.doc.lastChild;
        
        // Dacă ultimul bloc de text nu e gol (cum e la un fișier nou doar cu titlu "# nume")
        if (lastNode && lastNode.textContent.trim() !== '') {
          editor.commands.insertContentAt(editor.state.doc.content.size, '<p></p>');
        }
        
        editor.commands.focus('end');
        return;
      }
    }

    // 3. CLICK ÎN STÂNGA TEXTULUI (Marginea goală)
    // Orice click oriunde în partea stângă (de la marginea ecranului până fix la litere)
    if (e.clientX <= pmRect.left + 20) {
      // "Sondăm" poziția exactă de pe marginea stângă a textului, la înălțimea click-ului tău.
      // Math.max(pmRect.left + 5, 5) previne ieșirea accidentală din coordonate.
      const coords = { left: Math.max(pmRect.left + 5, 5), top: e.clientY };
      const posAtCoords = editor.view.posAtCoords(coords);

      if (posAtCoords) {
        // Punem cursorul exact acolo.
        // Asta funcționează perfect și în interiorul code block-urilor (```),
        // plasând cursorul fix în fața primei litere de pe rândul specific pe care ai dat click.
        editor.commands.focus(posAtCoords.pos);
        return;
      }
    }

    // 4. CLICK ÎN GOL PE DREAPTA -> Focus fix la capătul acelui rând
    if (!e.target.closest('.ProseMirror')) {
      const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
      if (pos && pos.pos !== null) {
        editor.commands.focus(pos.pos);
      } else {
        editor.commands.focus('end');
      }
    }
  }, [editor]);

  const setCodeBlockLanguage = (language) => {
    setSelectedLanguage(language);
    if (editor) {
      editor.chain().focus().updateAttributes("codeBlock", { language }).run();
    }
  };

  const handleImageUpload = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        const url = await uploadImage(file);
        if (url && editor) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      }
    };
    input.click();
  }, [editor, uploadImage]);

  if (!editor) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col w-full" data-testid="editor">
      <div className="border-b border-border px-2 sm:px-4 py-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 bg-background/50 backdrop-blur-xl w-full">
        <div className="flex items-center gap-1 flex-wrap w-full md:w-auto pb-2 md:pb-0">
          <span className="text-sm font-medium mr-2">{fileName}</span>
          
          {fileContext === "group" && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded text-xs mr-2">
              <Users className="w-3 h-3" />
              Shared
            </span>
          )}
          {fileContext === "external" && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-500 rounded text-xs mr-2">
              <Server className="w-3 h-3" />
              External
            </span>
          )}
          {fileContext === "common" && (
            <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-500/10 text-blue-500 rounded text-xs mr-2">
              <Users className="w-3 h-3" />
              Common
            </span>
          )}
          
          <MenuButton
            active={editor.isActive("heading", { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("heading", { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("heading", { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </MenuButton>
          
          <div className="w-px h-6 bg-border mx-2" />
          
          <MenuButton
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("strike")}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("highlight")}
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            title="Highlight"
          >
            <Highlighter className="w-4 h-4" />
          </MenuButton>
          
          <div className="w-px h-6 bg-border mx-2" />
          
          <MenuButton
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            title="Ordered List"
          >
            <ListOrdered className="w-4 h-4" />
          </MenuButton>
          
          <div className="w-px h-6 bg-border mx-2" />
          
          <MenuButton
            active={editor.isActive("blockquote")}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={editor.isActive("codeBlock")}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            title="Code Block"
          >
            <Code className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={false}
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            title="Horizontal Rule"
          >
            <Minus className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            active={false}
            onClick={handleImageUpload}
            title="Insert Image"
          >
            {isUploadingImage ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ImageIcon className="w-4 h-4" />
            )}
          </MenuButton>
          
          {editor.isActive("codeBlock") && (
            <Select value={selectedLanguage} onValueChange={setCodeBlockLanguage}>
              <SelectTrigger className="w-32 h-8 ml-2" data-testid="language-select">
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-auto justify-end border-t border-border md:border-t-0 pt-2 md:pt-0">
          {isSaving ? (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          ) : hasChanges ? (
            <span className="text-xs text-amber-500">Unsaved changes</span>
          ) : lastSavedAt ? (
            <span className="text-xs text-green-500 flex items-center gap-1">
              <Check className="w-3 h-3" />
              Saved
            </span>
          ) : null}
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualSave}
            disabled={isSaving || !hasChanges}
            className="h-8"
            data-testid="save-button"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="ml-2">Save</span>
          </Button>
        </div>
      </div>

      <div 
        ref={editorContainerRef}
        className="flex-1 overflow-y-auto cursor-text w-full"
        onClick={handleEditorAreaClick}
      >
        <div className="w-full h-full py-8 px-4 md:px-12 min-h-full relative">
          
          {/* Bloc CSS injectat pentru Typora/Notepad++ Style FARA numaratoare*/}
          <style>{`
            .tiptap-editor {
              min-height: 100%;
            }
            .tiptap-editor > * {
              margin-top: 0.15em !important;
              margin-bottom: 0.15em !important;
              line-height: 1.5;
            }
          `}</style>

          <EditorContent editor={editor} className="h-full" />
        </div>
      </div>
    </div>
  );
};